
import { toast } from "sonner";
import { useCallback } from "react";
import { safeToastError } from "@/components/ui/toastManager";

export type ErrorWithMessage = {
  message: string;
  status?: number;
  code?: string;
  details?: unknown;
};

/**
 * Normalizes different error types into a consistent format
 */
export function normalizeError(error: unknown): ErrorWithMessage {
  if (error instanceof Error) {
    return {
      message: error.message,
      details: error
    };
  }
  
  if (typeof error === 'string') {
    return { message: error };
  }
  
  if (typeof error === 'object' && error !== null) {
    // Handle Supabase error format
    if ('message' in error && typeof (error as any).message === 'string') {
      return {
        message: (error as any).message,
        code: (error as any).code,
        status: (error as any).status,
        details: error
      };
    }
    
    // Handle JSON stringifiable errors
    return {
      message: 'Errore sconosciuto',
      details: JSON.stringify(error)
    };
  }
  
  return { message: 'Errore sconosciuto' };
}

/**
 * Hook per gestire gli errori in modo consistente nell'applicazione
 */
export function useErrorHandler() {
  /**
   * Gestisce l'errore mostrando un toast e loggando i dettagli
   */
  const handleError = useCallback((error: unknown, customMessage?: string) => {
    const normalizedError = normalizeError(error);
    
    // Log detailed error for debugging
    console.error('Error details:', normalizedError);
    
    // Show user-friendly toast notification
    safeToastError("Errore", {
      description: customMessage || normalizedError.message || "Si è verificato un errore"
    });
    
    return normalizedError;
  }, []);
  
  return { handleError };
}

/**
 * Utility per gestire errori nei component di classe o al di fuori dei componenti React
 */
export function handleGlobalError(error: unknown, customMessage?: string) {
  const normalizedError = normalizeError(error);
  
  console.error('Global error:', normalizedError);
  
  safeToastError("Errore", {
    description: customMessage || normalizedError.message || "Si è verificato un errore"
  });
  
  return normalizedError;
}
